/**
 * Where a dragged window (or the nav) would land, as plain geometry over
 * client-space rects, so the drop rules are tested without a browser.
 */
import type { Edge, Zone } from "./workspace";

export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const ORDER: readonly Edge[] = ["left", "right", "top", "bottom"];

/** The edge with the smallest distance; ties go to the earlier one in `ORDER`. */
function nearest(distances: Record<Edge, number>): Edge {
  return ORDER.reduce((best, edge) => (distances[edge] < distances[best] ? edge : best));
}

const inside = (rect: Rect, x: number, y: number) =>
  x >= rect.left && x <= rect.left + rect.width && y >= rect.top && y <= rect.top + rect.height;

/** The zone of a window under the pointer: the centre box (the middle `centre` fraction each way, default 0.5) is "center"; otherwise the edge whose side of the window the point is relatively nearest (compare distance to each edge normalised by width/height). */
export function zoneAt(rect: Rect, x: number, y: number, centre = 0.5): Zone | null {
  if (!inside(rect, x, y) || rect.width <= 0 || rect.height <= 0) return null;
  const nx = (x - rect.left) / rect.width;
  const ny = (y - rect.top) / rect.height;
  if (Math.abs(nx - 0.5) <= centre / 2 && Math.abs(ny - 0.5) <= centre / 2) return "center";
  return nearest({ left: nx, right: 1 - nx, top: ny, bottom: 1 - ny });
}

/**
 * How far (px) the pointer must go past a zone's boundary before the preview leaves
 * it. Without it, a pointer resting on the line between two zones flipped the
 * preview back and forth with every sub-pixel move.
 */
export const DROP_HYSTERESIS_PX = 16;

/**
 * Whether the point still counts as `zone` with `slack` px of grace: the centre box
 * grown by it, or for an edge zone, outside the centre box shrunk by it and no more
 * than `slack` further from that edge than from the nearest other one.
 */
function holdsZone(rect: Rect, x: number, y: number, zone: Zone, slack: number, centre: number): boolean {
  if (rect.width <= 0 || rect.height <= 0) return false;
  if (!inside(grow(rect, slack), x, y)) return false;
  const sx = slack / rect.width;
  const sy = slack / rect.height;
  const nx = (x - rect.left) / rect.width;
  const ny = (y - rect.top) / rect.height;
  const inCentre = (by: number) => Math.abs(nx - 0.5) <= centre / 2 + by * sx && Math.abs(ny - 0.5) <= centre / 2 + by * sy;
  if (zone === "center") return inCentre(1);
  if (inCentre(-1)) return false;
  const distance: Record<Edge, number> = { left: nx, right: 1 - nx, top: ny, bottom: 1 - ny };
  const slackOf = (edge: Edge) => (edge === "left" || edge === "right" ? sx : sy);
  return ORDER.every((edge) => edge === zone || distance[zone] - slackOf(zone) <= distance[edge] + slackOf(edge));
}

const grow = (rect: Rect, by: number): Rect => ({ left: rect.left - by, top: rect.top - by, width: rect.width + 2 * by, height: rect.height + 2 * by });

/** `zoneAt`, but a zone the pointer was already in holds until it is `slack` px clearly out of it. */
export function stickyZoneAt(rect: Rect, x: number, y: number, previous: Zone | null, slack = DROP_HYSTERESIS_PX, centre = 0.5): Zone | null {
  if (previous && holdsZone(rect, x, y, previous, slack, centre)) return previous;
  return zoneAt(rect, x, y, centre);
}

/** `edgeAt`, but the edge the pointer was in holds until it is `slack` px past the band. */
export function stickyEdgeAt(rect: Rect, x: number, y: number, previous: Edge | null, band = 28, slack = DROP_HYSTERESIS_PX): Edge | null {
  if (previous && inside(rect, x, y)) {
    const distance = { left: x - rect.left, right: rect.left + rect.width - x, top: y - rect.top, bottom: rect.top + rect.height - y };
    if (distance[previous] <= band + slack) return previous;
  }
  return edgeAt(rect, x, y, band);
}

/** Where a dragged window, tab or view would land: an edge of the whole workspace, or a window's zone. */
export type DropTarget = { kind: "edge"; edge: Edge } | { kind: "window"; id: string; zone: Zone };

/** The workspace as a drag sees it, measured once when the drag starts (client coordinates). */
export interface DropGeometry {
  root: Rect;
  /** In tree order; `bar` for a minimised window, drawn as a bar or a strip. */
  windows: Array<{ id: string; rect: Rect; bar: boolean }>;
  maximizedId: string | null;
  /** One window: no edge bands, since there is nothing to put beside. */
  single: boolean;
}

export const sameTarget = (a: DropTarget | null, b: DropTarget | null): boolean =>
  a === b ||
  (a !== null &&
    b !== null &&
    (a.kind === "edge" ? b.kind === "edge" && a.edge === b.edge : b.kind === "window" && a.id === b.id && a.zone === b.zone));

/**
 * The target under the pointer. A bar wins over the edge band it often sits in;
 * then the workspace's edges; then the window under the pointer. `previous` is the
 * target shown so far, and holds within its hysteresis, so the preview does not
 * flutter on a boundary — between two zones, two windows, or a window and an edge.
 */
export function dropTargetAt(geo: DropGeometry, x: number, y: number, previous: DropTarget | null = null): DropTarget | null {
  const { root, windows, maximizedId, single } = geo;
  const rectOf = (id: string) => (maximizedId ? (id === maximizedId ? root : null) : windows.find((w) => w.id === id)?.rect ?? null);
  // The previous target first: inside its grace, nothing else takes over.
  if (previous?.kind === "edge" && !single) {
    if (stickyEdgeAt(root, x, y, previous.edge) === previous.edge) return previous;
  } else if (previous?.kind === "window") {
    const rect = rectOf(previous.id);
    if (rect) {
      const zone = stickyZoneAt(rect, x, y, previous.zone);
      if (zone === previous.zone) return previous;
    }
  }
  if (!maximizedId) {
    for (const w of windows) {
      const zone = w.bar ? zoneAt(w.rect, x, y) : null;
      if (zone) return { kind: "window", id: w.id, zone };
    }
  }
  const edge = single ? null : edgeAt(root, x, y);
  if (edge) return { kind: "edge", edge };
  for (const w of windows) {
    const rect = rectOf(w.id);
    const zone = rect ? zoneAt(rect, x, y) : null;
    if (zone) {
      // Moving within the same window keeps its sticky zone rather than the raw one.
      const held = previous?.kind === "window" && previous.id === w.id ? stickyZoneAt(rect!, x, y, previous.zone) : zone;
      return { kind: "window", id: w.id, zone: held ?? zone };
    }
  }
  return null;
}

/**
 * Which slot (0…n) of a tab strip a tab dragged to `x` would drop into, from the
 * tabs' rects measured when the drag began — never the live ones, which the
 * opening gap is moving.
 */
export function tabSlotAt(tabs: Array<{ left: number; width: number }>, x: number): number {
  const at = tabs.findIndex((tab) => x < tab.left + tab.width / 2);
  return at < 0 ? tabs.length : at;
}

/**
 * How far (px) tab `index` slides while tab `from` is dragged over slot `slot`, given
 * every tab's width: the tabs between where it was and where it would land move one
 * dragged-tab's width toward its old place, and the dragged tab (dimmed) moves into
 * the gap they open — so the strip shows the order the drop will make.
 */
export function tabShift(index: number, from: number, slot: number | null, widths: number[]): number {
  if (slot === null) return 0;
  const to = slot > from ? slot - 1 : slot;
  if (to === from) return 0;
  const width = widths[from] ?? 0;
  const between = (lo: number, hi: number) => widths.slice(lo, hi + 1).reduce((sum, w) => sum + w, 0);
  if (index === from) return to > from ? between(from + 1, to) : -between(to, from - 1);
  if (to > from && index > from && index <= to) return -width;
  if (to < from && index >= to && index < from) return width;
  return 0;
}

/** A strip along the whole workspace's edges (default 28px): which edge the pointer is in, or null. Corners → the nearer edge. */
export function edgeAt(rect: Rect, x: number, y: number, band = 28): Edge | null {
  if (!inside(rect, x, y)) return null;
  const distances = { left: x - rect.left, right: rect.left + rect.width - x, top: y - rect.top, bottom: rect.top + rect.height - y };
  const edge = nearest(distances);
  return distances[edge] <= band ? edge : null;
}

/** The height of a window's title bar, where its tabs are: a centre drop lights it up. */
export const TAB_STRIP_PX = 32;

/**
 * The rect to draw as the drop preview: the half of the window on that side, or for
 * the centre — which adds a tab — a strip along its top where the tab will appear.
 */
export function previewRect(rect: Rect, zone: Zone): Rect {
  return zone === "center" ? { ...rect, height: Math.min(TAB_STRIP_PX, rect.height) } : share(rect, zone, 1 / 2);
}

/** For an edge of the whole workspace: a third along that edge. */
export function edgePreviewRect(rect: Rect, edge: Edge): Rect {
  return share(rect, edge, 1 / 3);
}

function share(rect: Rect, edge: Edge, fraction: number): Rect {
  const width = rect.width * fraction;
  const height = rect.height * fraction;
  if (edge === "left") return { ...rect, width };
  if (edge === "right") return { ...rect, left: rect.left + rect.width - width, width };
  if (edge === "top") return { ...rect, height };
  return { ...rect, top: rect.top + rect.height - height, height };
}

/** Where a dragged nav would dock: the viewport edge nearest the point. */
export function dockAt(viewport: { width: number; height: number }, x: number, y: number): Edge {
  return nearest({ left: x, right: viewport.width - x, top: y, bottom: viewport.height - y });
}
